import React, { useState, useEffect } from 'react';
import { ImageSlide } from '../services/dealsApiService';
import { generateDeepLinkUrl, Environment } from '../services/DeepLinkService.ts';
import './DealsCarousel.css';

interface DealsCarouselProps {
  slides: ImageSlide[];
  autoPlayInterval?: number; // Default 5000ms
  onSpecialLinkClick?: (specialId: string) => void; // Callback for special/deal links
  onImageLoad?: () => void; // Callback when first image loads
  menuType?: 'medical' | 'recreational';
}

/**
 * Get current environment for deep link generation
 */
const getEnvironment = (): Environment => {
  const env = process.env.REACT_APP_ENV;
  return env === 'qa' ? 'qa' : 'production';
};

const DealsCarousel: React.FC<DealsCarouselProps> = ({
  slides,
  autoPlayInterval = 5000,
  onSpecialLinkClick,
  onImageLoad,
  menuType,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Handle first image load
  const handleImageLoad = () => {
    if (!imageLoaded) {
      setImageLoaded(true);
      onImageLoad?.();
    }
  };

  // Minimum swipe distance (in px) to trigger slide change
  const minSwipeDistance = 50;

  // Auto-rotation effect
  useEffect(() => {
    if (slides.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, autoPlayInterval);

    return () => clearInterval(timer);
  }, [currentIndex, slides.length, autoPlayInterval]);

  if (!slides || slides.length === 0) {
    return null;
  }

  // Touch event handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setTouchEnd(0); // Reset
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      // Swipe left - next slide
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }

    if (isRightSwipe) {
      // Swipe right - previous slide
      setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
    }

    // Reset
    setTouchStart(0);
    setTouchEnd(0);
  };

  // Render slide with optional link wrapper
  const renderSlide = (slide: ImageSlide, index: number) => {
    const image = (
      <img
        src={slide.desktop || slide.mobile}
        alt=""
        className={`carousel-image ${imageLoaded ? 'loaded' : ''}`}
        draggable={false}
        onLoad={index === 0 ? handleImageLoad : undefined}
      />
    );

    // Regulatory links (.gov) take priority and open in new tab
    if (slide.regulatoryLink) {
      return (
        <a
          href={slide.regulatoryLink}
          className="carousel-link carousel-regulatory-link"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open regulatory information in new tab"
        >
          {image}
        </a>
      );
    }

    // Deep links navigate within the app
    if (slide.deepLink) {
      // Special links (Dutchie deals) should update URL params without navigating
      if (slide.deepLink.linkType === 'special' && slide.deepLink.specialId && onSpecialLinkClick) {
        return (
          <button
            type="button"
            onClick={() => onSpecialLinkClick(slide.deepLink!.specialId!)}
            className="carousel-link carousel-special-link"
            aria-label="Filter menu by this deal"
          >
            {image}
          </button>
        );
      }

      // Filter and product links navigate to full URL
      const deepLinkConfig = menuType ? { ...slide.deepLink, menuType } : slide.deepLink;
      const url = generateDeepLinkUrl(deepLinkConfig, getEnvironment());
      return (
        <a href={url} className="carousel-link">
          {image}
        </a>
      );
    }

    return image;
  };

  return (
    <div
      className="deals-carousel"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div
        className="carousel-track"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {slides.map((slide, index) => (
          <div key={slide.id || index} className="carousel-slide">
            {renderSlide(slide, index)}
          </div>
        ))}
      </div>

      {slides.length > 1 && (
        <div className="carousel-indicators">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DealsCarousel;
